import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { trackExperimentInfo } from '@lib/tracking/google-tag-manager/events'
import { useExperimentService, useUserService } from '../services'
import {
  UrlBasedUsageTracker,
  isToolsFreeUseLimitToTrialTestTreatment,
  toolsFreeUseLimitToTrialLimits,
} from '@lib/utils/tool-usage'
import { PageContext } from '@contexts/page-context'

interface UseFreeLimitToTrialExperiment {
  usageTracker: UrlBasedUsageTracker | undefined
  showTrialModal: boolean
  setShowTrialModal: (show: boolean) => void
  isToolsFreeUseLimitExperiment: boolean
}

export const useFreeLimitToTrialExperiment = (): UseFreeLimitToTrialExperiment => {
  const { slug } = useContext(PageContext)
  const { currentUser } = useUserService()
  const { getToolsFreeUseLimitToTrialAssignment, experimentsWithAssignedTreatment } = useExperimentService()
  const [toolsFreeUseLimitAssignment] = useState(() => getToolsFreeUseLimitToTrialAssignment())
  const [showTrialModal, setShowTrialModal] = useState(false)

  useEffect(() => {
    trackExperimentInfo(slug, experimentsWithAssignedTreatment)
  }, [slug, experimentsWithAssignedTreatment])

  const handleLimitReached = useCallback(() => {
    setShowTrialModal(true)
  }, [])

  const usageTracker = useMemo(() => {
    if (isToolsFreeUseLimitToTrialTestTreatment(toolsFreeUseLimitAssignment)) {
      return new UrlBasedUsageTracker(
        toolsFreeUseLimitToTrialLimits[toolsFreeUseLimitAssignment],
        false,
        undefined,
        currentUser
      )
    }
    return undefined
  }, [toolsFreeUseLimitAssignment, currentUser])

  // Add and clean up event listener
  useEffect(() => {
    if (usageTracker) {
      usageTracker.addEventListener('limitreached', handleLimitReached)
      return () => {
        usageTracker.removeEventListener('limitreached', handleLimitReached)
      }
    }
  }, [usageTracker, handleLimitReached])

  return {
    usageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsFreeUseLimitExperiment: isToolsFreeUseLimitToTrialTestTreatment(toolsFreeUseLimitAssignment),
  }
}
